import { Badge, Dropdown, Option } from '@fluentui/react-components';
import { useCallback } from 'react';

import { useDatasetVersions } from '../../../hooks/useDatasetVersions/useDatasetVersions.js';
import type { DatasetVersion } from '../../../services/dataHubService.interface.js';

import type { VersionViewInfo } from './DatasetDetailView.interface.js';
import { useStyles } from './DatasetVersionDropdown.style.js';

interface DatasetVersionDropdownProps {
  datasetId: string;
  latestVersion: number;
  latestStatus: string;
  versionView: VersionViewInfo | null;
  onVersionChange: (view: VersionViewInfo | null) => void;
}

function statusColor(status: string) {
  return status === 'Published'
    ? ('success' as const)
    : status === 'Draft'
      ? ('important' as const)
      : ('subtle' as const);
}

export function DatasetVersionDropdown({
  datasetId,
  latestVersion,
  latestStatus,
  versionView,
  onVersionChange,
}: DatasetVersionDropdownProps) {
  const styles = useStyles();
  const { versions } = useDatasetVersions(datasetId);

  const dataVersion = versionView?.version ?? latestVersion;

  const handleVersionSelect = useCallback(
    (_: unknown, data: { optionValue?: string }) => {
      const selected = data.optionValue;
      if (!selected) return;
      if (selected === 'latest') {
        onVersionChange(null);
        return;
      }
      const ver = versions.find(
        (v: DatasetVersion) => String(v.version) === selected,
      );
      if (ver) {
        onVersionChange({
          version: ver.version,
          status: ver.status,
          createdAt: ver.createdAt,
        });
      }
    },
    [versions, onVersionChange],
  );

  return (
    <Dropdown
      size="small"
      className={styles.versionDropdown}
      value={`v${dataVersion}${versionView ? ` · ${versionView.status}` : ` · ${latestStatus} (latest)`}`}
      selectedOptions={[versionView ? String(versionView.version) : 'latest']}
      onOptionSelect={handleVersionSelect}
    >
      <Option
        value="latest"
        text={`v${latestVersion} · ${latestStatus} (latest)`}
      >
        v{latestVersion} ·{' '}
        <Badge
          appearance="filled"
          color={statusColor(latestStatus)}
          size="small"
        >
          {latestStatus}
        </Badge>{' '}
        (latest)
      </Option>
      {versions
        .filter((v) => v.version !== latestVersion)
        .map((v) => (
          <Option
            key={v.version}
            value={String(v.version)}
            text={`v${v.version} · ${v.status}`}
          >
            v{v.version} ·{' '}
            <Badge
              appearance="filled"
              color={statusColor(v.status)}
              size="small"
            >
              {v.status}
            </Badge>
          </Option>
        ))}
    </Dropdown>
  );
}
